import pathToRegexp, { type Key } from '../path-to-regexp';
import { RouteProps, match } from '.';

interface CompilePathOptions {
  end: boolean;
  strict: boolean;
  sensitive: boolean;
}

interface CompilePathResult {
  regexp: RegExp;
  keys: Key[];
}

const cache: { [key: string]: { [path: string]: CompilePathResult } } = {};
const cacheLimit = 10000;
let cacheCount = 0;

function compilePath(
  path: string,
  options: CompilePathOptions
): CompilePathResult {
  const cacheKey = `${options.end}${options.strict}${options.sensitive}`;
  const pathCache = cache[cacheKey] || (cache[cacheKey] = {});

  if (pathCache[path]) return pathCache[path];

  const keys: Key[] = [];
  const regexp = pathToRegexp(path, keys, options);
  const result = { regexp, keys };

  if (cacheCount < cacheLimit) {
    pathCache[path] = result;
    cacheCount++;
  }

  return result;
}

/**
 * Public API for matching a URL pathname to a path.
 */
function matchPath<Params extends { [K in keyof Params]?: string }>(
  pathname: string,
  props: string | string[] | RouteProps
): match<Params> | null {
  if (typeof props === 'string' || Array.isArray(props)) {
    props = { path: props };
  }

  const { path, exact = false, strict = false, sensitive = false } = props;

  const paths = [].concat(path as any);

  return paths.reduce((matched: any, currentPath) => {
    if (!currentPath) return null;
    if (matched) return matched;

    const { regexp, keys } = compilePath(currentPath, {
      end: exact,
      strict,
      sensitive,
    });
    const regexMatch = regexp.exec(pathname);

    if (!regexMatch) return null;

    const [url, ...values] = regexMatch;
    const isExact = pathname === url;

    if (exact && !isExact) return null;

    return {
      path: currentPath, // the path used to match
      url: currentPath === '/' && url === '' ? '/' : url, // the matched portion of the URL
      isExact, // whether or not we matched exactly
      params: keys.reduce((memo, key, index) => {
        // @ts-expect-error indexing generic Params with string key
        memo[key.name] = values[index];
        return memo;
      }, {} as Params),
    };
  }, null);
}

export default matchPath;
